import React, { useContext, useEffect, useState, Fragment } from 'react'
import { useParams, Link, useLocation } from "react-router-dom";
import { Menu, Transition, Disclosure } from '@headlessui/react'
import { ChevronDown, Menu as MenuIcon, X } from 'lucide-react';
import styled from 'styled-components';
import { arrayIntersection, isDev, capitalizeFirstLetter } from '../utils';
import EventContext from '../contexts/EventContext'
import darkModeContext from '../contexts/DarkModeContext';
import NavTranslation from './NavTranslation';
import useApiUrlTranslation from './useApiUrlTranslation';
import NavBeta from './NavBeta';
import './NavEvent.css'; // Leaving for any critical non-bootstrap styles, but should be minimal now
import moment from 'moment';

const NavEvent = () => {
    const { t, i18n, apiUrlTranslation } = useApiUrlTranslation()
    const { darkMode } = useContext(darkModeContext); // Removed toggleDarkMode if not used in Nav
    const { idEvent, id_cycle_lang } = useParams();
    const eventContext = useContext(EventContext);
    const [eventEventComposantList, setEventEventComposantList] = useState([]);
    const { getEventByIdEvent, currentEvent } = eventContext;
    const [eventCycleLangList, setEventCycleLangList] = useState(false)
    const location = useLocation();
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    // Styled component for the top link - keeping implementation but moving to Tailwind classes in render
    const MyglobalvillageLink = styled(Link)`
        &:hover {
            text-decoration: underline;
        }
    `

    useEffect(() => {
        const getEventCycleLangList = async () => {
            // Logic kept identical
            await fetch(`${apiUrlTranslation}?action=getEventCycleLangList&params= WHERE id_event=${idEvent} AND forum="packs" AND publier!="n"`)
                .then(res => res.json())
                .then(res => {
                    setEventCycleLangList(res)
                })
        }

        const getEventEventComposant = async () => {
            // Logic kept identical
            await fetch(`${apiUrlTranslation}?action=getEventEventComposant&req_suite= WHERE id_event=${idEvent}`)
                .then(res => res.json())
                .then(res => setEventEventComposantList(res))
        }

        getEventEventComposant();
        getEventByIdEvent(idEvent)
        getEventCycleLangList()
    }, [i18n.resolvedLanguage]) // eslint-disable-line react-hooks/exhaustive-deps


    if (
        (["2270", "3013", "3014"].includes(id_cycle_lang)
            || (currentEvent && [1731, '1731', "1747", "1736"].includes(currentEvent.id_event))
        ) && window.location.pathname.includes("/signup")) {
        return <NavTranslation />
    }

    if (!currentEvent) return null;

    // Helper functions for class logic
    const isHidden = (condition) => condition ? 'hidden' : '';
    const isActive = (path) => location.pathname === path ? 'text-primary font-bold' : '';

    return (
        <>
            {/* Top Bar */}
            <div className=' relative dark:bg-neutral-950 flex justify-end pr-3 py-1' style={{ zIndex: 1000 }}>
                <MyglobalvillageLink
                    to="/"
                    style={{ fontSize: '12px' }}
                    className="flex items-center space-x-2 mx-3 text-xs dark:text-white hover:underline transition-colors">
                    <div className="flex space-x-2 items-center">
                        <img
                            src="https://www.mlg-consulting.com/manager_cc/docs/archives/230906125631_output-onlinepngtools.png"
                            alt="My Global Village"
                            className={`${darkMode && 'invert-custom'} h-8 ${currentEvent.event_ligne.categorie == "cdt" ? "hidden" : ''}`}
                        />
                    </div>
                </MyglobalvillageLink>
                {isDev && <NavTranslation />}
            </div>

            {/* Main Navigation */}
            <nav className={`
                sticky top-0 z-40 w-full border-y border-neutral-100 bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/60
                dark:bg-neutral-950/95 dark:border-neutral-800 dark:text-white
                ${location.pathname.includes('signup') ? 'hidden' : ''}
                px-4 md:px-6 lg:pr-10 lg:mr-20
            `}>
                <div className="flex h-20 items-center justify-between">

                    {/* Brand / Logo Section */}
                    <div className="flex items-center gap-4">
                        <Link className="flex items-center gap-2" to={`/events/${currentEvent.id_event}`}>
                            {currentEvent.id_event == 1927 &&
                                <div className='flex space-x-2'>
                                    <img src={`https://www.mlg-consulting.com/manager_cc/docs/archives/250310175239_sans-titre-1-01.png`} alt="" className="h-[70px]" />
                                    <img src="https://www.mlg-consulting.com/manager_cc/docs/archives//thumbs/250304151130_brique-picto-21_1000x0.png" alt="" className="h-[70px]" />
                                </div>
                            }
                            {![1927, "2139", "2265"].includes(currentEvent.id_event) && (
                                <img
                                    src={`${currentEvent && currentEvent.logo ? currentEvent.logos.small : currentEvent.event_ligne.el_visuels.small}`}
                                    alt={currentEvent.nom}
                                    className="h-[70px] object-contain bg-white p-2  border border-neutral-200"
                                />
                            )}
                        </Link>

                        {currentEvent.id_event != 1927 && (
                            <div className='hidden md:block dark:text-white text-sm leading-tight'>
                                <span className="font-semibold block">{currentEvent.nom}</span>
                                <span className="block text-xs text-muted-foreground">
                                    <i className="far fa-calendar-minus mr-1"></i>

                                    {currentEvent.precision_date != "" ? currentEvent.precision_date : `${t('global.du')} ${moment(currentEvent.event_start).format('DD')} ${t('global.au')} ${moment(currentEvent.event_end).format('DD MMMM YYYY')}`}
                                </span>
                                {![1927, "2139", "2265"].includes(currentEvent.id_event) && (
                                    <span className="block text-xs text-muted-foreground">
                                        <i className="fas fa-map-marker-alt mr-1"></i>
                                        {currentEvent.lieu.lieu_nom} - {currentEvent.lieu.lieu_ville}, {currentEvent.lieu.lieu_pays}
                                    </span>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Mobile Menu Button */}
                    <div className="flex md:hidden">
                        <button
                            type="button"
                            className="-m-2.5 inline-flex items-center justify-center rounded-md p-2.5 text-gray-700 dark:text-white"
                            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                        >
                            <span className="sr-only">Open main menu</span>
                            {mobileMenuOpen ? <X className="h-6 w-6" aria-hidden="true" /> : <MenuIcon className="h-6 w-6" aria-hidden="true" />}
                        </button>
                    </div>

                    {/* Desktop Navigation Links */}
                    <div className={`hidden md:flex md:gap-x-6 items-center ${location.pathname.includes('login-redirect') ? 'invisible' : ''}`}>


                        {currentEvent.event_ligne.categorie != "cdt" &&
                            <Link
                                className="text-sm font-semibold leading-6 text-gray-900 dark:text-white hover:text-primary transition-colors capitalize"
                                to={currentEvent ? `/events/${currentEvent.id_event}/` : ''}
                            >
                                {t('event_nav.accueil')}
                            </Link>}

                        {/* Dropdown: A Propos */}

                        {currentEvent.event_ligne.categorie != "cdt" && <Menu as="div" className={`relative ${[1820, "2139", "2265"].includes(idEvent) ? 'hidden' : ''}`}>
                            <Menu.Button className="flex items-center gap-x-1 text-sm font-semibold leading-6 text-gray-900 dark:text-white hover:text-primary transition-colors">
                                {capitalizeFirstLetter(t('event_nav.a_propos'))}
                                <ChevronDown className="h-4 w-4" aria-hidden="true" />
                            </Menu.Button>
                            <NavTransition>
                                <Menu.Items className="absolute right-0 z-10 mt-2 w-56 origin-top-right rounded-md   dark:bg-neutral-900 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                                    <div className="py-1">
                                        {!["2139", "2265"].includes(currentEvent.id_event) && (
                                            <MenuItem href={currentEvent ? `/events/${currentEvent.id_event}/highligths/temps_forts` : ''} hidden={currentEvent.id_event == 1822}>
                                                {t('event_nav.temps_forts')}
                                            </MenuItem>
                                        )}
                                        {idEvent == 1822 && (
                                            <>
                                                <MenuItem href={`/events/1822/page/5860`}>{t('event_nav.evenement_qq_mots')}</MenuItem>
                                                <MenuItem href={`/events/1822/page/5859`}>{t('event_nav.info_pratiques')}</MenuItem>
                                            </>
                                        )}
                                        <MenuItemLink to={`/events/${currentEvent.id_event}/event-description`} hidden={['1838', '1822'].includes(currentEvent.id_event)}>
                                            {t('event_nav.evenement_qq_mots')}
                                        </MenuItemLink>
                                        <MenuItemLink to={`/events/${currentEvent.id_event}/commite-organisation-event`}>
                                            {t('event_nav.comite_orga')}
                                        </MenuItemLink>
                                        {!["2139", "2265"].includes(currentEvent.id_event) && (
                                            <MenuItemLink to={`/events/${currentEvent.id_event}/partenaires`} hidden={!(currentEvent.event_categorie == "cdt" || ![1809].includes(idEvent))}>
                                                {t('event_nav.partenaires')}
                                            </MenuItemLink>
                                        )}
                                        {!["2139", "2265"].includes(currentEvent.id_event) && (
                                            <MenuItem href={`/events/${currentEvent.id_event}/coordinateurs-collectif`}>Coordinateurs</MenuItem>
                                        )}
                                        {["2139", "2265"].includes(currentEvent.id_event) && (
                                            <>
                                                <MenuItemLink to={`/events/${currentEvent.id_event}/candidats/`} target="_blank">Candidats</MenuItemLink>
                                                <MenuItemLink to={`/events/${currentEvent.id_event}/annuaire-jury/`} target="_blank">{t('event_nav.membres_du_jury')}</MenuItemLink>
                                            </>
                                        )}
                                    </div>
                                </Menu.Items>
                            </NavTransition>
                        </Menu>
                        }

                        {/* Dropdown: Programme */}
                        {!["2139", "2265"].includes(currentEvent.id_event) && currentEvent.event_ligne.categorie != "cdt" && (
                            <Menu as="div" className="relative">
                                <Menu.Button className="flex items-center gap-x-1 text-sm font-semibold leading-6 text-gray-900 dark:text-white hover:text-primary transition-colors">
                                    {capitalizeFirstLetter(t('event_nav.programme'))}
                                    <ChevronDown className="h-4 w-4" aria-hidden="true" />
                                </Menu.Button>
                                <NavTransition>
                                    <Menu.Items className="absolute right-0 z-10 mt-2 w-56 origin-top-right rounded-md  dark:bg-neutral-900 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                                        <div className="py-1">
                                            <MenuItemLink to={`/events/${currentEvent.id_event}/programm`}>{t('event_nav.programme_complet')}</MenuItemLink>

                                            {idEvent == 1820 && (
                                                <>
                                                    <MenuItem href={`/events/${currentEvent.id_event}/annuaire-techxplorateurs/`}>{t('event_nav.techxplorations_annuaire')}</MenuItem>
                                                    <MenuItem href={`/events/${currentEvent.id_event}/login-redirect?redirectUrl=https://www.myglobalvillage.com/events/${currentEvent.id_event}/signup/techxplorateur/c/{id_contact}?formisvisible=1&id_formulaire=31&form-name=techxplorateur&lng=fr`}>
                                                        Demande de participation
                                                    </MenuItem>
                                                </>
                                            )}

                                            {arrayIntersection(eventEventComposantList.map(item => item.id_event_composant), ["2", "32", "14"]).length > 0 && (
                                                <MenuItemLink to={`/events/${currentEvent.id_event}/programm/pitchs`}>{t('event_nav.pitching_session')}</MenuItemLink>
                                            )}

                                            {arrayIntersection(eventEventComposantList.map(item => item.id_event_composant), ["39"]).length > 0 && (
                                                <MenuItemLink to={`/events/${currentEvent.id_event}/programm/techreviews`}>Programme des Techreviews</MenuItemLink>
                                            )}

                                            {arrayIntersection(eventEventComposantList.map(item => item.id_event_composant), ["15"]).length > 0 && (
                                                <MenuItemLink to={`/events/${currentEvent.id_event}/programm/networking`}>Les soirées</MenuItemLink>
                                            )}

                                            {arrayIntersection(eventEventComposantList.map(item => item.id_event_composant), ["38"]).length > 0 && (
                                                <MenuItemLink to={`/events/${currentEvent.id_event}/programm/texplorations`}>{t('event_nav.techxplorations_programme')}</MenuItemLink>
                                            )}

                                            <MenuItemLink to={`/events/${currentEvent.id_event}/annuaire-jury/`} target="_blank">{t('event_nav.membres_du_jury')}</MenuItemLink>
                                            <MenuItemLink to={`/events/${currentEvent.id_event}/candidats/`} target="_blank">Rising stars</MenuItemLink>
                                        </div>
                                    </Menu.Items>
                                </NavTransition>
                            </Menu>
                        )}

                        {/* Dropdown: Demonstrateurs */}
                        {arrayIntersection(eventEventComposantList.map(item => item.id_event_composant), ["3"]).length > 0 && (
                            <Menu as="div" className="relative">
                                <Menu.Button className="flex items-center gap-x-1 text-sm font-semibold leading-6 text-gray-900 dark:text-white hover:text-primary transition-colors">
                                    {capitalizeFirstLetter(t('event_nav.demonstrateurs'))}
                                    <ChevronDown className="h-4 w-4" aria-hidden="true" />
                                </Menu.Button>
                                <NavTransition>
                                    <Menu.Items className="absolute right-0 z-10 mt-2 w-56 origin-top-right rounded-md bg-white dark:bg-neutral-900 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                                        <div className="py-1">
                                            <MenuItem href={`/events/${currentEvent.id_event}/event-component/3`}>{t('event_nav.presentation')}</MenuItem>
                                        </div>
                                    </Menu.Items>
                                </NavTransition>
                            </Menu>
                        )}

                        {/* Dropdown: Parcours de visite */}
                        {arrayIntersection(eventEventComposantList.map(item => item.id_event_composant), ["6"]).length > 0 && (
                            <Menu as="div" className="relative">
                                <Menu.Button className="flex items-center gap-x-1 text-sm font-semibold leading-6 text-gray-900 dark:text-white hover:text-primary transition-colors">
                                    {capitalizeFirstLetter(t('event_nav.parcours_de_visite'))}
                                    <ChevronDown className="h-4 w-4" aria-hidden="true" />
                                </Menu.Button>
                                <NavTransition>
                                    <Menu.Items className="absolute right-0 z-10 mt-2 w-56 origin-top-right rounded-md bg-white dark:bg-neutral-900 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                                        <div className="py-1">
                                            <MenuItem href={`/events/${currentEvent.id_event}/event-component/6/`}>{t('event_nav.presentation')}</MenuItem>
                                            <MenuItemLink to={`/events/${currentEvent.id_event}/programm/parcours`}>{t('event_nav.programme_parcours_de_visite')}</MenuItemLink>
                                            {currentEvent.event_ligne.categorie != "cdt" && (
                                                <MenuItemLink to={`/events/${currentEvent.id_event}/signup/startup`}>{t('event_nav.positionner_parcours_de_visite')}</MenuItemLink>
                                            )}
                                            <MenuItem href={`/events/${currentEvent.id_event}/candidats2/`}>Exposants</MenuItem>
                                        </div>
                                    </Menu.Items>
                                </NavTransition>
                            </Menu>
                        )}

                        {/* Simple Links */}
                        {/* {!["2139"].includes(currentEvent.id_event) && (
                            <Link
                                className="text-sm font-semibold leading-6 text-gray-900 dark:text-white hover:text-primary transition-colors capitalize"
                                to={currentEvent ? `/events/${currentEvent.id_event}/infos-pratiques` : ''}
                            >
                                {t('event_nav.info_pratiques')}
                            </Link>
                        )} */}

                        {currentEvent.id_event == 1789 && (
                            <Link
                                className="text-sm font-semibold leading-6 text-gray-900 dark:text-white hover:text-primary transition-colors capitalize"
                                to={currentEvent ? `/events/${currentEvent.id_event}/pays-recap` : ''}
                            >
                                {t('event_nav.pays_affilies')}
                            </Link>
                        )}

                        {["2139", "2265"].includes(currentEvent.id_event) && (
                            <Link
                                className="text-sm font-semibold leading-6 text-gray-900 dark:text-white hover:text-primary transition-colors capitalize"
                                to={currentEvent ? `/events/${currentEvent.id_event}/candidats` : ''}
                            >
                                Candidats
                            </Link>
                        )}

                        {/* Registration Button */}
                        {currentEvent.event_etat != "bilan" && currentEvent.id_event == 2015 && (
                            <Link
                                className='rounded-full bg-pink-600 px-4 py-2 text-sm font-bold text-white shadow-sm hover:bg-pink-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-pink-600 capitalize'
                                to={`/events/${currentEvent.id_event}/packs`}
                            >
                                {t('event_nav.inscription')}
                            </Link>
                        )}

                        {/* Bilan Dropdown */}
                        {['bilan'].includes(currentEvent.event_etat) && (
                            <Menu as="div" className="relative">
                                <Menu.Button className="flex items-center gap-x-1 text-sm font-semibold leading-6 text-gray-900 dark:text-white hover:text-primary transition-colors">
                                    {capitalizeFirstLetter("Bilan")}
                                    <ChevronDown className="h-4 w-4" aria-hidden="true" />
                                </Menu.Button>
                                <NavTransition>
                                    <Menu.Items className="absolute right-0 z-10 mt-2 w-56 origin-top-right rounded-md bg-white dark:bg-neutral-900 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                                        <div className="py-1">
                                            <MenuItemLink to={`/events/${currentEvent.id_event}/highligths/bilan`} target="_blank">Bilan de l'édition {currentEvent.event_an}</MenuItemLink>
                                            <MenuItemLink to={`/events/${currentEvent.id_event}/palmares`} target="_blank">
                                                <i className="fas fa-trophy mr-1"></i> {t('global.palmares')}
                                            </MenuItemLink>
                                            <MenuItemLink to={`/events/${currentEvent.id_event}/replay`}>
                                                <i className="fas fa-video mr-1 text-red-700"></i> Replay
                                            </MenuItemLink>
                                        </div>
                                    </Menu.Items>
                                </NavTransition>
                            </Menu>
                        )}

                    </div>
                </div>
            </nav>

            {/* Mobile Menu Overlay */}
            <div className={`fixed inset-0 z-50 transform transition-transform duration-300 md:hidden ${mobileMenuOpen ? 'translate-x-0' : 'translate-x-full'}`}>
                {/* Backdrop */}
                <div className="fixed inset-0 bg-black/25" onClick={() => setMobileMenuOpen(false)} aria-hidden="true" />

                {/* Panel */}
                <div className="fixed inset-y-0 right-0 z-50 w-full overflow-y-auto bg-white dark:bg-neutral-950 px-6 py-6 sm:max-w-sm sm:ring-1 sm:ring-gray-900/10">
                    <div className="flex items-center justify-between">
                        <div className="-m-1.5 p-1.5">
                            <span className="sr-only">{currentEvent.nom}</span>
                            <img
                                className="h-8 w-auto"
                                src={`${currentEvent && currentEvent.logo ? currentEvent.logos.small : currentEvent.event_ligne.el_visuels.small}`}
                                alt=""
                            />
                        </div>
                        <button
                            type="button"
                            className="-m-2.5 rounded-md p-2.5 text-gray-700 dark:text-white"
                            onClick={() => setMobileMenuOpen(false)}
                        >
                            <span className="sr-only">Close menu</span>
                            <X className="h-6 w-6" aria-hidden="true" />
                        </button>
                    </div>
                    <div className="mt-6 flow-root">
                        <div className="-my-6 divide-y divide-gray-500/10">
                            <div className="space-y-2 py-6">

                                <Link
                                    to={currentEvent ? `/events/${currentEvent.id_event}/` : ''}
                                    className="-mx-3 block rounded-lg px-3 py-2 text-base font-semibold leading-7 text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-neutral-800"
                                    onClick={() => setMobileMenuOpen(false)}
                                >
                                    {t('event_nav.accueil')}
                                </Link>

                                {/* A PROPOS Mobile */}
                                <Disclosure as="div" className={`-mx-3 ${[1820, "2139", "2265"].includes(idEvent) ? 'hidden' : ''}`}>
                                    {({ open }) => (
                                        <>
                                            <Disclosure.Button className="flex w-full items-center justify-between rounded-lg py-2 pl-3 pr-3.5 text-base font-semibold leading-7 text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-neutral-800">
                                                {capitalizeFirstLetter(t('event_nav.a_propos'))}
                                                <ChevronDown
                                                    className={`h-5 w-5 flex-none ${open ? 'rotate-180' : ''}`}
                                                    aria-hidden="true"
                                                />
                                            </Disclosure.Button>
                                            <Disclosure.Panel className="mt-2 space-y-2">
                                                {!["2139", "2265"].includes(currentEvent.id_event) && (
                                                    <MobileMenuItem href={currentEvent ? `/events/${currentEvent.id_event}/highligths/temps_forts` : ''} hidden={currentEvent.id_event == 1822} setMobileMenuOpen={setMobileMenuOpen}>
                                                        {t('event_nav.temps_forts')}
                                                    </MobileMenuItem>
                                                )}
                                                {idEvent == 1822 && (
                                                    <>
                                                        <MobileMenuItem href={`/events/1822/page/5860`} setMobileMenuOpen={setMobileMenuOpen}>{t('event_nav.evenement_qq_mots')}</MobileMenuItem>
                                                        <MobileMenuItem href={`/events/1822/page/5859`} setMobileMenuOpen={setMobileMenuOpen}>{t('event_nav.info_pratiques')}</MobileMenuItem>
                                                    </>
                                                )}
                                                <MobileMenuItemLink to={`/events/${currentEvent.id_event}/event-description`} hidden={['1838', '1822'].includes(currentEvent.id_event)} setMobileMenuOpen={setMobileMenuOpen}>
                                                    {t('event_nav.evenement_qq_mots')}
                                                </MobileMenuItemLink>
                                                <MobileMenuItemLink to={`/events/${currentEvent.id_event}/commite-organisation-event`} setMobileMenuOpen={setMobileMenuOpen}>
                                                    {t('event_nav.comite_orga')}
                                                </MobileMenuItemLink>
                                                {!["2139", "2265"].includes(currentEvent.id_event) && (
                                                    <MobileMenuItemLink to={`/events/${currentEvent.id_event}/partenaires`} hidden={!(currentEvent.event_categorie == "cdt" || ![1809].includes(idEvent))} setMobileMenuOpen={setMobileMenuOpen}>
                                                        {t('event_nav.partenaires')}
                                                    </MobileMenuItemLink>
                                                )}
                                                {!["2139", "2265"].includes(currentEvent.id_event) && (
                                                    <MobileMenuItem href={`/events/${currentEvent.id_event}/coordinateurs-collectif`} setMobileMenuOpen={setMobileMenuOpen}>Coordinateurs</MobileMenuItem>
                                                )}
                                                {["2139", "2265"].includes(currentEvent.id_event) && (
                                                    <>
                                                        <MobileMenuItemLink to={`/events/${currentEvent.id_event}/candidats/`} target="_blank" setMobileMenuOpen={setMobileMenuOpen}>Candidats</MobileMenuItemLink>
                                                        <MobileMenuItemLink to={`/events/${currentEvent.id_event}/annuaire-jury/`} target="_blank" setMobileMenuOpen={setMobileMenuOpen}>{t('event_nav.membres_du_jury')}</MobileMenuItemLink>
                                                    </>
                                                )}
                                            </Disclosure.Panel>
                                        </>
                                    )}
                                </Disclosure>

                                {/* PROGRAMME Mobile */}
                                {!["2139", "2265"].includes(currentEvent.id_event) && (
                                    <Disclosure as="div" className="-mx-3">
                                        {({ open }) => (
                                            <>
                                                <Disclosure.Button className="flex w-full items-center justify-between rounded-lg py-2 pl-3 pr-3.5 text-base font-semibold leading-7 text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-neutral-800">
                                                    {capitalizeFirstLetter(t('event_nav.programme'))}
                                                    <ChevronDown
                                                        className={`h-5 w-5 flex-none ${open ? 'rotate-180' : ''}`}
                                                        aria-hidden="true"
                                                    />
                                                </Disclosure.Button>
                                                <Disclosure.Panel className="mt-2 space-y-2">
                                                    <MobileMenuItemLink to={`/events/${currentEvent.id_event}/programm`} setMobileMenuOpen={setMobileMenuOpen}>{t('event_nav.programme_complet')}</MobileMenuItemLink>

                                                    {idEvent == 1820 && (
                                                        <>
                                                            <MobileMenuItem href={`/events/${currentEvent.id_event}/annuaire-techxplorateurs/`} setMobileMenuOpen={setMobileMenuOpen}>{t('event_nav.techxplorations_annuaire')}</MobileMenuItem>
                                                            <MobileMenuItem href={`/events/${currentEvent.id_event}/login-redirect?redirectUrl=https://www.myglobalvillage.com/events/${currentEvent.id_event}/signup/techxplorateur/c/{id_contact}?formisvisible=1&id_formulaire=31&form-name=techxplorateur&lng=fr`} setMobileMenuOpen={setMobileMenuOpen}>
                                                                Demande de participation
                                                            </MobileMenuItem>
                                                        </>
                                                    )}

                                                    {arrayIntersection(eventEventComposantList.map(item => item.id_event_composant), ["2", "32", "14"]).length > 0 && (
                                                        <MobileMenuItemLink to={`/events/${currentEvent.id_event}/programm/pitchs`} setMobileMenuOpen={setMobileMenuOpen}>{t('event_nav.pitching_session')}</MobileMenuItemLink>
                                                    )}

                                                    {arrayIntersection(eventEventComposantList.map(item => item.id_event_composant), ["39"]).length > 0 && (
                                                        <MobileMenuItemLink to={`/events/${currentEvent.id_event}/programm/techreviews`} setMobileMenuOpen={setMobileMenuOpen}>Programme des Techreviews</MobileMenuItemLink>
                                                    )}

                                                    {arrayIntersection(eventEventComposantList.map(item => item.id_event_composant), ["15"]).length > 0 && (
                                                        <MobileMenuItemLink to={`/events/${currentEvent.id_event}/programm/networking`} setMobileMenuOpen={setMobileMenuOpen}>Les soirées</MobileMenuItemLink>
                                                    )}

                                                    {arrayIntersection(eventEventComposantList.map(item => item.id_event_composant), ["38"]).length > 0 && (
                                                        <MobileMenuItemLink to={`/events/${currentEvent.id_event}/programm/texplorations`} setMobileMenuOpen={setMobileMenuOpen}>{t('event_nav.techxplorations_programme')}</MobileMenuItemLink>
                                                    )}

                                                    <MobileMenuItemLink to={`/events/${currentEvent.id_event}/annuaire-jury/`} target="_blank" setMobileMenuOpen={setMobileMenuOpen}>{t('event_nav.membres_du_jury')}</MobileMenuItemLink>
                                                    <MobileMenuItemLink to={`/events/${currentEvent.id_event}/candidats/`} target="_blank" setMobileMenuOpen={setMobileMenuOpen}>Rising stars</MobileMenuItemLink>
                                                </Disclosure.Panel>
                                            </>
                                        )}
                                    </Disclosure>
                                )}

                                {/* DEMONSTRATEURS Mobile */}
                                {arrayIntersection(eventEventComposantList.map(item => item.id_event_composant), ["3"]).length > 0 && (
                                    <Disclosure as="div" className="-mx-3">
                                        {({ open }) => (
                                            <>
                                                <Disclosure.Button className="flex w-full items-center justify-between rounded-lg py-2 pl-3 pr-3.5 text-base font-semibold leading-7 text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-neutral-800">
                                                    {capitalizeFirstLetter(t('event_nav.demonstrateurs'))}
                                                    <ChevronDown
                                                        className={`h-5 w-5 flex-none ${open ? 'rotate-180' : ''}`}
                                                        aria-hidden="true"
                                                    />
                                                </Disclosure.Button>
                                                <Disclosure.Panel className="mt-2 space-y-2">
                                                    <MobileMenuItem href={`/events/${currentEvent.id_event}/event-component/3`} setMobileMenuOpen={setMobileMenuOpen}>{t('event_nav.presentation')}</MobileMenuItem>
                                                </Disclosure.Panel>
                                            </>
                                        )}
                                    </Disclosure>
                                )}

                                {/* PARCOURS Mobile */}
                                {arrayIntersection(eventEventComposantList.map(item => item.id_event_composant), ["6"]).length > 0 && (
                                    <Disclosure as="div" className="-mx-3">
                                        {({ open }) => (
                                            <>
                                                <Disclosure.Button className="flex w-full items-center justify-between rounded-lg py-2 pl-3 pr-3.5 text-base font-semibold leading-7 text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-neutral-800">
                                                    {capitalizeFirstLetter(t('event_nav.parcours_de_visite'))}
                                                    <ChevronDown
                                                        className={`h-5 w-5 flex-none ${open ? 'rotate-180' : ''}`}
                                                        aria-hidden="true"
                                                    />
                                                </Disclosure.Button>
                                                <Disclosure.Panel className="mt-2 space-y-2">
                                                    <MobileMenuItem href={`/events/${currentEvent.id_event}/event-component/6/`} setMobileMenuOpen={setMobileMenuOpen}>{t('event_nav.presentation')}</MobileMenuItem>
                                                    <MobileMenuItemLink to={`/events/${currentEvent.id_event}/programm/parcours`} setMobileMenuOpen={setMobileMenuOpen}>{t('event_nav.programme_parcours_de_visite')}</MobileMenuItemLink>
                                                    {currentEvent.event_ligne.categorie != "cdt" && (
                                                        <MobileMenuItemLink to={`/events/${currentEvent.id_event}/signup/startup`} setMobileMenuOpen={setMobileMenuOpen}>{t('event_nav.positionner_parcours_de_visite')}</MobileMenuItemLink>
                                                    )}
                                                    <MobileMenuItem href={`/events/${currentEvent.id_event}/candidats2/`} setMobileMenuOpen={setMobileMenuOpen}>Exposants</MobileMenuItem>
                                                </Disclosure.Panel>
                                            </>
                                        )}
                                    </Disclosure>
                                )}

                                {/* {!["2139"].includes(currentEvent.id_event) && (
                                    <Link
                                        to={currentEvent ? `/events/${currentEvent.id_event}/infos-pratiques` : ''}
                                        className="-mx-3 block rounded-lg px-3 py-2 text-base font-semibold leading-7 text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-neutral-800"
                                        onClick={() => setMobileMenuOpen(false)}
                                    >
                                        {t('event_nav.info_pratiques')}
                                    </Link>
                                )} */}

                                {currentEvent.id_event == 1789 && (
                                    <Link
                                        to={currentEvent ? `/events/${currentEvent.id_event}/pays-recap` : ''}
                                        className="-mx-3 block rounded-lg px-3 py-2 text-base font-semibold leading-7 text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-neutral-800"
                                        onClick={() => setMobileMenuOpen(false)}
                                    >
                                        {t('event_nav.pays_affilies')}
                                    </Link>
                                )}

                                {["2139", "2265"].includes(currentEvent.id_event) && (
                                    <Link
                                        to={currentEvent ? `/events/${currentEvent.id_event}/candidats` : ''}
                                        className="-mx-3 block rounded-lg px-3 py-2 text-base font-semibold leading-7 text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-neutral-800"
                                        onClick={() => setMobileMenuOpen(false)}
                                    >
                                        Candidats
                                    </Link>
                                )}

                                {currentEvent.event_etat != "bilan" && currentEvent.id_event == 2015 && (
                                    <Link
                                        to={`/events/${currentEvent.id_event}/packs`}
                                        className="-mx-3 block rounded-lg px-3 py-2 text-base font-semibold leading-7 text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-neutral-800 text-pink-600"
                                        onClick={() => setMobileMenuOpen(false)}
                                    >
                                        {t('event_nav.inscription')}
                                    </Link>
                                )}

                                {/* BILAN Mobile */}
                                {['bilan'].includes(currentEvent.event_etat) && (
                                    <Disclosure as="div" className="-mx-3">
                                        {({ open }) => (
                                            <>
                                                <Disclosure.Button className="flex w-full items-center justify-between rounded-lg py-2 pl-3 pr-3.5 text-base font-semibold leading-7 text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-neutral-800">
                                                    {capitalizeFirstLetter("Bilan")}
                                                    <ChevronDown
                                                        className={`h-5 w-5 flex-none ${open ? 'rotate-180' : ''}`}
                                                        aria-hidden="true"
                                                    />
                                                </Disclosure.Button>
                                                <Disclosure.Panel className="mt-2 space-y-2">
                                                    <MobileMenuItemLink to={`/events/${currentEvent.id_event}/highligths/bilan`} target="_blank" setMobileMenuOpen={setMobileMenuOpen}>Bilan de l'édition {currentEvent.event_an}</MobileMenuItemLink>
                                                    <MobileMenuItemLink to={`/events/${currentEvent.id_event}/palmares`} target="_blank" setMobileMenuOpen={setMobileMenuOpen}>
                                                        <i className="fas fa-trophy mr-1"></i> {t('global.palmares')}
                                                    </MobileMenuItemLink>
                                                    <MobileMenuItemLink to={`/events/${currentEvent.id_event}/replay`} setMobileMenuOpen={setMobileMenuOpen}>
                                                        <i className="fas fa-video mr-1 text-red-700"></i> Replay
                                                    </MobileMenuItemLink>
                                                </Disclosure.Panel>
                                            </>
                                        )}
                                    </Disclosure>
                                )}

                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <NavBeta />
        </>
    )
}

// Helpers components for cleaner wrapper
const NavTransition = ({ children }) => (
    <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
    >
        {children}
    </Transition>
)

const MenuItem = ({ href, children, hidden = false, ...props }) => {
    if (hidden) return null;
    return (
        <Menu.Item>
            {({ active }) => (
                <a
                    href={href}
                    className={`
                        ${active ? 'bg-gray-100 dark:bg-neutral-800 text-gray-900 dark:text-white' : 'text-gray-700 dark:text-gray-200'}
                        block px-4 py-2 text-sm
                    `}
                    {...props}
                >
                    {capitalizeFirstLetter(children)}
                </a>
            )}
        </Menu.Item>
    )
}

const MenuItemLink = ({ to, children, hidden = false, ...props }) => {
    if (hidden) return null;
    return (
        <Menu.Item>
            {({ active }) => (
                <Link
                    to={to}
                    className={`
                        ${active ? 'bg-gray-100 dark:bg-neutral-800 text-gray-900 dark:text-white' : 'text-gray-700 dark:text-gray-200'}
                        block px-4 py-2 text-sm
                    `}
                    {...props}
                >
                    {capitalizeFirstLetter(children)}
                </Link>
            )}
        </Menu.Item>
    )
}

const MobileMenuItem = ({ href, children, hidden = false, setMobileMenuOpen, ...props }) => {
    if (hidden) return null;
    return (
        <a
            href={href}
            className="block rounded-lg py-2 pl-6 pr-3 text-sm font-semibold leading-7 text-gray-900 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-neutral-900"
            onClick={() => setMobileMenuOpen(false)}
            {...props}
        >
            {capitalizeFirstLetter(children)}
        </a>
    )
}

const MobileMenuItemLink = ({ to, children, hidden = false, setMobileMenuOpen, ...props }) => {
    if (hidden) return null;
    return (
        <Link
            to={to}
            className="block rounded-lg py-2 pl-6 pr-3 text-sm font-semibold leading-7 text-gray-900 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-neutral-900"
            onClick={() => setMobileMenuOpen(false)}
            {...props}
        >
            {capitalizeFirstLetter(children)}
        </Link>
    )
}

export default NavEvent;